import { getAuth0PublicConfig } from '@sstdev/lib_config';

/**
 * Resolves browser/mobile-safe Auth0 config for the `uiRuntimeConfig` facade.
 *
 * The returned property names intentionally match the Auth0 SDK options and
 * existing UI call sites. Internally, lookups go through `lib/config` so browser
 * runtime globals and environment variables share one provider path.
 *
 * `AUTH0_AUDIENCE` is optional and independent from the client id/domain pair.
 * It keeps token requests on the logical Environment's public API when the UI
 * is reached through a concrete candidate Deployment Target. Omitting it
 * preserves the established browser-origin fallback in Auth0 clients.
 *
 * Source priority:
 * 1. browser/runtime globals injected by `environmentVariables.js`
 * 2. current `process.env` values
 *
 * @param {{
 *     brand?: string,
 *     environment?: string,
 *     env?: Record<string, string | undefined>,
 *     globalObject?: Record<string, unknown>
 * }} options
 * @returns {{
 *     AUTH0_AUDIENCE?: string,
 *     AUTH0_CLIENT_ID: string | undefined,
 *     AUTH0_CONNECTION_NAME: string,
 *     AUTH0_DOMAIN: string | undefined
 * }}
 */
export function getAuth0RuntimeConfig({
    brand,
    environment,
    env = getDefaultEnv(),
    globalObject = getDefaultGlobalObject()
}) {
    const auth0Config = getAuth0PublicConfig({ brand, environment, env, globalObject });

    return {
        ...(auth0Config.audience ? { AUTH0_AUDIENCE: auth0Config.audience } : {}),
        AUTH0_CLIENT_ID: auth0Config.clientId,
        AUTH0_CONNECTION_NAME: auth0Config.connectionName,
        AUTH0_DOMAIN: auth0Config.domain
    };
}

/** @returns {Record<string, string | undefined>} */
function getDefaultEnv() {
    const runtimeGlobal = /** @type {{ process?: { env?: Record<string, string | undefined> } }} */ (globalThis);
    return runtimeGlobal.process?.env ?? {};
}

/** @returns {Record<string, unknown>} */
function getDefaultGlobalObject() {
    return typeof globalThis === 'undefined' ? {} : globalThis;
}
